/* eslint-disable import/no-anonymous-default-export */
import type { AxiosResponse } from "axios";
import apiClient from "@utilities/apiClient";
import EndpointsConst from "@consts/EndpointsConst";

type AddBlogPostPayload = Omit<BlogPost, "id" | "imgLoc"> & {
  imgLoc?: string;
};

type EditBlogPostPayload = Pick<BlogPost, "title" | "subtitle" | "content"> & {
  id?: string;
  imgLoc?: string;
};

interface AddBlogPostImageResponse {
  imageUrl: string;
}

class BlogService {
  /**
   * [POST]
   * Submits the new blog post to the database
   * @param blogPost - The blog post's title, subtitle, content, image location, and date
   */
  addBlogPost(blogPost: AddBlogPostPayload): Promise<AxiosResponse> {
    return apiClient.post(EndpointsConst.API.BLOG.ADD_BLOG_POST, blogPost);
  }

  /**
   * [POST]
   * Submits the new blog post image to content system
   * @param imageFile - Form data containing the image file
   */
  addBlogPostImage(
    imageFile: FormData,
  ): Promise<AxiosResponse<AddBlogPostImageResponse>> {
    return apiClient.post(
      EndpointsConst.API.BLOG.ADD_BLOG_POST_IMAGE,
      imageFile,
    );
  }

  /**
   * [DELETE]
   * Deletes Blog Post and respective image from database & content system
   * @param id - The id of the blog post to delete
   * @param encodedImgLoc - The URI-encoded location of the post's image
   */
  deleteBlogPost(id: string, encodedImgLoc: string): Promise<AxiosResponse> {
    return apiClient.delete(EndpointsConst.API.BLOG.DELETE_BLOG_POST, {
      params: { id, encodedImgLoc },
    });
  }

  /**
   * [POST]
   * Edits the blog post
   * @param blogPost - The updated blog post fields
   */
  editBlogPost(blogPost: EditBlogPostPayload): Promise<AxiosResponse> {
    return apiClient.post(EndpointsConst.API.BLOG.EDIT_BLOG_POST, blogPost);
  }

  /**
   * [GET]
   * Gets all blog posts
   */
  getAllBlogPosts(): Promise<AxiosResponse<BlogPost[]>> {
    return apiClient.get(EndpointsConst.API.BLOG.GET_ALL_BLOG_POSTS);
  }

  /**
   * [GET]
   * Gets blog post by ID
   * @param id - The id of the blog post to retrieve
   */
  getBlogPostById(id: number): Promise<AxiosResponse<BlogPost>> {
    return apiClient.get(EndpointsConst.API.BLOG.GET_BLOG_POST_BY_ID, {
      params: { id },
    });
  }
}

export default new BlogService();
